import { toDate } from '@art-suite/art-core-ts';
import { ScanDataInMemory, ScanTemplateRoot } from '@kaiizen/kai-scan-template-system';
import { FetchUploadableBinaryData, ZodLessApiClient } from '@kaiizen/zod-less-api';
import { getScanPhotoImageUrl, MaxPhotoDimension } from './ApiLib/scanPhoto';
import {
  ApiAbortScanVideoMultipartUpload,
  ApiAbortScanVideoMultipartUploadResponse,
  ApiActivityLogEventType,
  ApiAllOrgSettings,
  ApiBatchUploadOrgPropertiesResponse,
  ApiCompleteIssueVideoUpload,
  ApiCompleteScanVideoAudioUpload,
  ApiCompleteScanVideoAudioUploadResponse,
  ApiCompleteScanVideoMultipartUpload,
  ApiCompleteScanVideoUpload,
  ApiCompleteScanVideoUploadResponse,
  ApiCreateApiKey,
  ApiCreateOrg,
  ApiCreateOrgContractor,
  ApiCreateOrgMarket,
  ApiCreateOrgProduct,
  ApiCreatePropertyProject,
  ApiCreateScan,
  ApiCreateScanDataSuggestion,
  ApiCreateScanNote,
  ApiCreateScanPhoto,
  ApiCreateScanTemplate,
  ApiFindPropertyProjects,
  ApiHandoffPropertyProject,
  ApiInitIssueVideoUpload,
  ApiInitScanVideoAudioUpload,
  ApiInitScanVideoAudioUploadResponse,
  ApiInitScanVideoMultipartUpload,
  ApiInitScanVideoMultipartUploadResponse,
  ApiInitScanVideoUpload,
  ApiIssueVideoSubmission,
  ApiIssueVideoSubmissionListResponse,
  ApiOperationOf,
  ApiOperationStatus,
  ApiOperationType,
  ApiReportType,
  ApiScanVideoAudio,
  ApiScanVideoMultipartStatusResponse,
  ApiScanVideoSubmission,
  ApiSetFeatureFlag,
  ApiSetSupplierAccess,
  ApiSignScanVideoUploadParts,
  ApiSignScanVideoUploadPartsResponse,
  ApiTakePropertyProjectWork,
  ApiUpdateApiKey,
  ApiUpdateOrg,
  ApiUpdateOrgContractor,
  ApiUpdateOrgMarket,
  ApiUpdateOrgProduct,
  ApiUpdateOrgProperty,
  ApiUpdateOrgSettings,
  ApiUpdatePropertyProject,
  ApiUpdateScan,
  ApiUpdateScanDataSuggestion,
  ApiUpdateScanDataSuggestionsByScanVideo,
  ApiUpdateScanTemplate,
  ApiUpdateUser,
  ApiUserOrgRole,
  ApiVivifyOrgProperty,
  AuthInitiateSmsRequestPayload,
  AuthValidateSmsRequestPayload,
  CombinedProductSearchRequestQuery,
  JevEvaluatePayload,
  PropertyProjectQuery,
  RetailerProductSearchRequestQuery,
  ScanLifecycleStatuses,
} from './ApiSchemas';
import { KaiProApiEndpoints, MAX_SCAN_PART_TREE_HISTORY_PAGE_SIZE, type ScanPartHistoryRecord } from './KaiProApiEndpoints';

const BEGINNING_OF_TIME = toDate(0);
type SafeRecordWithCustomerMetadata<T> = Omit<T, 'customerMetadata'> & { customerMetadata?: Record<string, any> };

export type ScanPartTreeHistoryOptions = { from?: Date; to?: Date; toScanDataId?: string; limit?: number };

/**
 * `limit` has no cap (omit it to fetch the full history). Requests are made `pageSize` records at a time,
 * which is itself capped at MAX_SCAN_PART_TREE_HISTORY_PAGE_SIZE.
 */
export type AllScanPartTreeHistoryOptions = { from?: Date; to?: Date; limit?: number; pageSize?: number };

const fetchAllScanPartTreeHistoryPages = async (
  fetchPage: (options: ScanPartTreeHistoryOptions) => Promise<ScanPartHistoryRecord[]>,
  { from, to, limit = Infinity, pageSize = MAX_SCAN_PART_TREE_HISTORY_PAGE_SIZE }: AllScanPartTreeHistoryOptions = {},
): Promise<ScanPartHistoryRecord[]> => {
  const records: ScanPartHistoryRecord[] = [];
  let cursor: Pick<ScanPartTreeHistoryOptions, 'to' | 'toScanDataId'> = { to };
  while (records.length < limit) {
    const pageLimit = Math.min(pageSize, MAX_SCAN_PART_TREE_HISTORY_PAGE_SIZE, limit - records.length);
    const page = await fetchPage({ from, ...cursor, limit: pageLimit });
    records.push(...page);
    if (page.length < pageLimit) break;
    const lastRecord = page[page.length - 1];
    cursor = { to: toDate(lastRecord.updatedAt), toScanDataId: lastRecord.id };
  }
  return records;
};

const getScanPartTreeHistoryQuery = (scanPartId: string, options?: ScanPartTreeHistoryOptions) => ({
  scanPartId,
  from: options?.from ? toDate(options.from).toISOString() : undefined,
  to: options?.to ? toDate(options.to).toISOString() : undefined,
  toScanDataId: options?.toScanDataId,
  limit: options?.limit,
});

export class KaiProApiClient extends ZodLessApiClient {
  /** Anyone gets `success`; super admins also get server health `details`. */
  heartbeat() {
    return this.jsonRequest(KaiProApiEndpoints.heartbeat, {});
  }

  // auth
  validateSession() {
    return this.jsonRequest(KaiProApiEndpoints.validateSession, {});
  }
  initiateReceiveSmsAuth(phoneNumber: string, messageType: 'sms' | 'whatsapp') {
    return this.jsonRequest(KaiProApiEndpoints.initiateReceiveSmsAuth, { payload: { phoneNumber, messageType } });
  }
  initiateSendSmsAuth(payload: AuthInitiateSmsRequestPayload) {
    return this.jsonRequest(KaiProApiEndpoints.initiateSendSmsAuth, { payload });
  }
  initiateSendEmailAuth(email: string) {
    return this.jsonRequest(KaiProApiEndpoints.initiateSendEmailAuth, { payload: { email } });
  }
  validateSendSmsAuth(payload: AuthValidateSmsRequestPayload) {
    return this.jsonRequest(KaiProApiEndpoints.validateSendSmsAuth, { payload });
  }
  validateSendEmailAuth(email: string, code: string) {
    return this.jsonRequest(KaiProApiEndpoints.validateSendEmailAuth, { payload: { email, code } });
  }
  syncExpoTokens(expoTokens: string[]) {
    return this.jsonRequest(KaiProApiEndpoints.syncExpoTokens, { payload: { expoTokens } });
  }
  getExpoTokens() {
    return this.jsonRequest(KaiProApiEndpoints.getExpoTokens, {});
  }
  deleteAccount() {
    return this.jsonRequest(KaiProApiEndpoints.deleteAccount, {});
  }
  checkReceiveSmsAuth(sessionId: string, phoneNumber: string, verificationToken: string) {
    return this.jsonRequest(KaiProApiEndpoints.checkReceiveSmsAuth, { key: sessionId, payload: { phoneNumber, verificationToken } });
  }
  switchOrg(orgId: string, options?: { userId?: string }) {
    return this.jsonRequest(KaiProApiEndpoints.switchOrg, { key: orgId, query: options?.userId ? { userId: options.userId } : {} });
  }
  getQrCode(sessionId: string, phoneNumber: string, verificationToken: string, messageType: 'sms' | 'whatsapp') {
    return this.jsonRequest(KaiProApiEndpoints.getQrCode, { key: sessionId, query: { phoneNumber, verificationToken, messageType } });
  }

  // user
  getUser(userIdOrPhoneNumber: string) {
    return this.jsonRequest(KaiProApiEndpoints.getUser, { key: userIdOrPhoneNumber });
  }
  getUserPermissions(userId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getUserPermissions, { key: userId });
  }
  getUsers(options?: { search?: string }) {
    return this.jsonRequest(KaiProApiEndpoints.getUsers, {
      query: options?.search?.trim() ? { search: options.search.trim() } : {},
    });
  }
  searchUsers(query: string) {
    return this.jsonRequest(KaiProApiEndpoints.getUserSearch, { query: { query } });
  }
  updateUser(userId: string, payload: ApiUpdateUser) {
    return this.jsonRequest(KaiProApiEndpoints.updateUser, { key: userId, payload });
  }
  getApiKeys() {
    return this.jsonRequest(KaiProApiEndpoints.getApiKeys, {});
  }
  createApiKey(payload: ApiCreateApiKey) {
    return this.jsonRequest(KaiProApiEndpoints.createApiKey, { payload });
  }
  updateApiKey(apiKeyId: string, payload: ApiUpdateApiKey) {
    return this.jsonRequest(KaiProApiEndpoints.updateApiKey, { key: apiKeyId, payload });
  }
  revokeApiKey(apiKeyId: string) {
    return this.jsonRequest(KaiProApiEndpoints.revokeApiKey, { key: apiKeyId });
  }
  getCurrentFeatureFlags() {
    return this.jsonRequest(KaiProApiEndpoints.getCurrentFeatureFlags, {});
  }
  getAllFeatureFlagRecords() {
    return this.jsonRequest(KaiProApiEndpoints.getAllFeatureFlagRecords, {});
  }
  setFeatureFlag(payload: ApiSetFeatureFlag) {
    return this.jsonRequest(KaiProApiEndpoints.setFeatureFlag, { payload });
  }
  deleteFeatureFlag(featureFlagId: string) {
    return this.jsonRequest(KaiProApiEndpoints.deleteFeatureFlag, { key: featureFlagId });
  }

  // supplier access
  getOrgSuppliers() {
    return this.jsonRequest(KaiProApiEndpoints.getOrgSuppliers, {});
  }
  getSupplierAccessOverview() {
    return this.jsonRequest(KaiProApiEndpoints.getSupplierAccessOverview, {});
  }
  setSupplierAccess(payload: ApiSetSupplierAccess) {
    return this.jsonRequest(KaiProApiEndpoints.setSupplierAccess, { payload });
  }
  deleteSupplierAccess(supplierAccessId: string) {
    return this.jsonRequest(KaiProApiEndpoints.deleteSupplierAccess, { key: supplierAccessId });
  }

  // notifications
  getNotifications(options?: { limit?: number; cursor?: string }) {
    return this.jsonRequest(KaiProApiEndpoints.getNotifications, {
      query: { ...(options?.limit ? { limit: options.limit } : {}), ...(options?.cursor ? { cursor: options.cursor } : {}) },
    });
  }
  markNotificationsRead(options: { ids?: string[]; markAll?: boolean }) {
    return this.jsonRequest(KaiProApiEndpoints.markNotificationsRead, { payload: options });
  }
  getProjectActivityLog(
    projectId: string,
    options?: { limit?: number; cursor?: string; order?: 'asc' | 'desc'; eventTypes?: readonly ApiActivityLogEventType[] },
  ) {
    return this.jsonRequest(KaiProApiEndpoints.getProjectActivityLog, {
      key: projectId,
      query: {
        ...(options?.limit ? { limit: options.limit } : {}),
        ...(options?.cursor ? { cursor: options.cursor } : {}),
        ...(options?.order ? { order: options.order } : {}),
        ...(options?.eventTypes?.length ? { eventTypes: options.eventTypes.join(',') } : {}),
      },
    });
  }
  getPropertyActivityLog(
    orgPropertyId: string,
    options?: { limit?: number; cursor?: string; order?: 'asc' | 'desc'; eventTypes?: readonly ApiActivityLogEventType[] },
  ) {
    return this.jsonRequest(KaiProApiEndpoints.getPropertyActivityLog, {
      key: orgPropertyId,
      query: {
        ...(options?.limit ? { limit: options.limit } : {}),
        ...(options?.cursor ? { cursor: options.cursor } : {}),
        ...(options?.order ? { order: options.order } : {}),
        ...(options?.eventTypes?.length ? { eventTypes: options.eventTypes.join(',') } : {}),
      },
    });
  }

  // Org
  getOrg(orgId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getOrg, { key: orgId });
  }
  createOrg(payload: ApiCreateOrg) {
    return this.jsonRequest(KaiProApiEndpoints.createOrg, { payload });
  }
  updateOrg(orgId: string, payload: ApiUpdateOrg) {
    return this.jsonRequest(KaiProApiEndpoints.updateOrg, { key: orgId, payload });
  }
  deleteOrg(orgId: string) {
    return this.jsonRequest(KaiProApiEndpoints.deleteOrg, { key: orgId });
  }

  // Org Users
  addUserToOrg(orgId: string, userId: string, role: ApiUserOrgRole) {
    return this.jsonRequest(KaiProApiEndpoints.addUserToOrg, { key: orgId, query: { userId, role } });
  }
  addUserToOrgByPhoneNumber(orgId: string, phoneNumber: string | undefined, role: ApiUserOrgRole, name?: string, email?: string, userId?: string) {
    return this.jsonRequest(KaiProApiEndpoints.addUserToOrgByPhoneNumber, { key: orgId, payload: { phoneNumber, role, name, email, userId } });
  }
  removeUserFromOrg(orgId: string, userId: string) {
    return this.jsonRequest(KaiProApiEndpoints.removeUserFromOrg, { key: orgId, query: { userId } });
  }
  updateUserOrgRole(orgId: string, userId: string, role: ApiUserOrgRole) {
    return this.jsonRequest(KaiProApiEndpoints.updateUserOrgRole, { key: orgId, query: { userId, role } });
  }
  getOrgsByUserId(userId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getOrgsByUserId, { key: userId });
  }

  // Org
  getAllOrgs() {
    return this.jsonRequest(KaiProApiEndpoints.getAllOrgs, {});
  }
  getAllProductSupplierOrgs() {
    return this.jsonRequest(KaiProApiEndpoints.getAllProductSupplierOrgs, {});
  }

  // Org Property
  getOrgProperty(orgId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getOrgProperty, { key: orgId });
  }
  getOrgProperties(options?: { archived?: boolean }) {
    return this.jsonRequest(KaiProApiEndpoints.getOrgProperties, { query: options?.archived === true ? { archived: true } : {} });
  }

  vivifyOrgProperty(property: SafeRecordWithCustomerMetadata<ApiVivifyOrgProperty>) {
    return this.jsonRequest(KaiProApiEndpoints.vivifyOrgProperty, { payload: property });
  }
  batchUploadOrgProperties(
    file: FetchUploadableBinaryData,
    timeoutSeconds?: number,
    onProgress?: (progress: number) => void,
  ): Promise<ApiBatchUploadOrgPropertiesResponse> {
    return this.postFile(KaiProApiEndpoints.batchUploadOrgProperties, {} as any, file, timeoutSeconds, onProgress);
  }
  updateOrgProperty(orgPropertyId: string, payload: SafeRecordWithCustomerMetadata<ApiUpdateOrgProperty>) {
    return this.jsonRequest(KaiProApiEndpoints.updateOrgProperty, { key: orgPropertyId, payload });
  }
  searchProperties(query: string, options?: { limit?: number; includeArchived?: boolean }) {
    return this.jsonRequest(KaiProApiEndpoints.searchProperties, {
      query: { search: query, ...(options?.limit ? { limit: options.limit } : {}), ...(options?.includeArchived ? { includeArchived: true } : {}) },
    });
  }
  getCurrentOrg() {
    return this.jsonRequest(KaiProApiEndpoints.getCurrentOrg, {});
  }
  getOrgIdForResource(
    query: { scanId?: string; propertyProjectId?: string; orgPropertyId?: string; orgMarketId?: string; scanTemplateId?: string; orgContractorId?: string },
  ) {
    return this.jsonRequest(KaiProApiEndpoints.getOrgIdForResource, { query });
  }

  // Property Project
  getPropertyProject(projectId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getPropertyProject, { key: projectId });
  }
  getPropertyProjectsByOrgPropertyId(orgId: string, options?: { archived?: boolean; limit?: number }) {
    return this.jsonRequest(KaiProApiEndpoints.getPropertyProjectsByOrgPropertyId, {
      key: orgId,
      query: { ...(options?.archived === true ? { archived: true } : {}), ...(options?.limit ? { limit: options.limit.toString() } : {}) },
    });
  }
  getActivePropertyProjectsByMarketId(orgMarketId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getActivePropertyProjectsByMarketId, { key: orgMarketId });
  }
  getActivePropertyProjectsForUser(options?: { limit?: number }) {
    return this.jsonRequest(KaiProApiEndpoints.getActivePropertyProjectsForUser, { query: { limit: options?.limit } });
  }
  getActivePropertyProjects(options?: { limit?: number; search?: string; orgMarketId?: string; assignedToCurrentUser?: boolean; sortBy?: string }) {
    return this.jsonRequest(KaiProApiEndpoints.getActivePropertyProjects, {
      query: {
        limit: options?.limit,
        ...(options?.search ? { search: options.search } : {}),
        ...(options?.orgMarketId ? { orgMarketId: options.orgMarketId } : {}),
        ...(options?.assignedToCurrentUser !== undefined ? { assignedToCurrentUser: options.assignedToCurrentUser } : {}),
        ...(options?.sortBy ? { sortBy: options.sortBy } : {}),
      },
    });
  }
  getPropertyProjects(options?: PropertyProjectQuery) {
    return this.jsonRequest(KaiProApiEndpoints.getPropertyProjects, { query: options ?? {} });
  }
  createPropertyProject(payload: ApiCreatePropertyProject) {
    return this.jsonRequest(KaiProApiEndpoints.createPropertyProject, { payload });
  }
  updatePropertyProject(propertyProjectId: string, payload: ApiUpdatePropertyProject) {
    return this.jsonRequest(KaiProApiEndpoints.updatePropertyProject, { key: propertyProjectId, payload });
  }
  handoffPropertyProject(propertyProjectId: string, payload: ApiHandoffPropertyProject) {
    return this.jsonRequest(KaiProApiEndpoints.handoffPropertyProject, { key: propertyProjectId, payload });
  }
  takePropertyProjectWork(propertyProjectId: string, payload: ApiTakePropertyProjectWork) {
    return this.jsonRequest(KaiProApiEndpoints.takePropertyProjectWork, { key: propertyProjectId, payload });
  }
  reactivatePropertyProject(propertyProjectId: string) {
    return this.jsonRequest(KaiProApiEndpoints.reactivatePropertyProject, { key: propertyProjectId });
  }
  findPropertyProjects(payload: ApiFindPropertyProjects) {
    return this.jsonRequest(KaiProApiEndpoints.findPropertyProjects, { payload });
  }

  // Street View
  getStreetViewImage({ address, width, height, heading, pitch, fov }: {
    address: string;
    width?: number;
    height?: number;
    heading?: number;
    pitch?: number;
    fov?: number;
  }): Promise<Blob> {
    return this.getBinary(KaiProApiEndpoints.getStreetViewImage, {
      query: {
        address,
        width: width?.toString(),
        height: height?.toString(),
        heading: heading?.toString(),
        pitch: pitch?.toString(),
        fov: fov?.toString(),
      },
    });
  }

  // Issue Video Upload
  initIssueVideoUpload(payload: ApiInitIssueVideoUpload) {
    return this.jsonRequest(KaiProApiEndpoints.initIssueVideoUpload, { payload });
  }
  completeIssueVideoUpload(payload: ApiCompleteIssueVideoUpload) {
    return this.jsonRequest(KaiProApiEndpoints.completeIssueVideoUpload, { payload });
  }
  getIssueVideoSubmission(issueVideoId: string): Promise<ApiIssueVideoSubmission> {
    return this.jsonRequest(KaiProApiEndpoints.getIssueVideoSubmission, { key: issueVideoId });
  }
  listIssueVideoSubmissions(query: { projectId?: string; scanId?: string; userId?: string; limit?: number }): Promise<ApiIssueVideoSubmissionListResponse> {
    return this.jsonRequest(KaiProApiEndpoints.listIssueVideoSubmissions, { query });
  }

  // Scan Video
  initScanVideoUpload(payload: ApiInitScanVideoUpload) {
    return this.jsonRequest(KaiProApiEndpoints.initScanVideoUpload, { payload });
  }
  completeScanVideoUpload(payload: ApiCompleteScanVideoUpload) {
    return this.jsonRequest(KaiProApiEndpoints.completeScanVideoUpload, { payload });
  }
  getScanVideo(scanVideoId: string): Promise<ApiScanVideoSubmission> {
    return this.jsonRequest(KaiProApiEndpoints.getScanVideo, { key: scanVideoId });
  }
  getScanVideoData(scanVideoId: string) {
    return this.getBinary(KaiProApiEndpoints.getScanVideoData, { key: scanVideoId });
  }

  // Scan Video Audio (narration segments)
  initScanVideoAudioUpload(payload: ApiInitScanVideoAudioUpload): Promise<ApiInitScanVideoAudioUploadResponse> {
    return this.jsonRequest(KaiProApiEndpoints.initScanVideoAudioUpload, { payload });
  }
  completeScanVideoAudioUpload(payload: ApiCompleteScanVideoAudioUpload): Promise<ApiCompleteScanVideoAudioUploadResponse> {
    return this.jsonRequest(KaiProApiEndpoints.completeScanVideoAudioUpload, { payload });
  }
  getScanVideoAudio(scanVideoAudioId: string): Promise<ApiScanVideoAudio> {
    return this.jsonRequest(KaiProApiEndpoints.getScanVideoAudio, { key: scanVideoAudioId });
  }
  getScanVideoAudiosById(scanVideoAudioIds: string[]) {
    if (scanVideoAudioIds.length === 0) return Promise.resolve([]);
    return this.jsonRequest(KaiProApiEndpoints.getScanVideoAudiosById, {
      payload: { scanVideoAudioIds },
    });
  }
  getScanVideoAudioData(scanVideoAudioId: string) {
    return this.getBinary(KaiProApiEndpoints.getScanVideoAudioData, { key: scanVideoAudioId });
  }

  getScanVideosById(scanVideoIds: string[], options?: { validate?: boolean }) {
    if (scanVideoIds.length === 0) return Promise.resolve([]);
    return this.jsonRequest(KaiProApiEndpoints.getScanVideosById, {
      query: options?.validate ? { validate: true } : {},
      payload: { scanVideoIds },
    });
  }

  // AI Scan Video Multipart Upload (large >=1GB walkthroughs)
  initScanVideoMultipartUpload(payload: ApiInitScanVideoMultipartUpload): Promise<ApiInitScanVideoMultipartUploadResponse> {
    return this.jsonRequest(KaiProApiEndpoints.initScanVideoMultipartUpload, { payload });
  }
  signScanVideoUploadParts(payload: ApiSignScanVideoUploadParts): Promise<ApiSignScanVideoUploadPartsResponse> {
    return this.jsonRequest(KaiProApiEndpoints.signScanVideoUploadParts, { payload });
  }
  completeScanVideoMultipartUpload(payload: ApiCompleteScanVideoMultipartUpload): Promise<ApiCompleteScanVideoUploadResponse> {
    return this.jsonRequest(KaiProApiEndpoints.completeScanVideoMultipartUpload, { payload });
  }
  abortScanVideoMultipartUpload(payload: ApiAbortScanVideoMultipartUpload): Promise<ApiAbortScanVideoMultipartUploadResponse> {
    return this.jsonRequest(KaiProApiEndpoints.abortScanVideoMultipartUpload, { payload });
  }
  getScanVideoMultipartStatus(videoId: string): Promise<ApiScanVideoMultipartStatusResponse> {
    return this.jsonRequest(KaiProApiEndpoints.getScanVideoMultipartStatus, { key: videoId });
  }

  // Scan Photo
  createScanPhoto(payload: ApiCreateScanPhoto, file: FetchUploadableBinaryData, timeoutSeconds?: number, onProgress?: (progress: number) => void) {
    return this.postFile(KaiProApiEndpoints.createScanPhoto, { payload } as any, file, timeoutSeconds, onProgress);
  }
  getScanPhoto(scanPhotoId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getScanPhoto, { key: scanPhotoId });
  }
  canCreateScanPhoto(scanId: string) {
    return this.jsonRequest(KaiProApiEndpoints.canCreateScanPhoto, { key: scanId });
  }
  getScanPhotoData(scanPhotoId: string, query?: { maxDimension?: MaxPhotoDimension }) {
    return this.getBinary(KaiProApiEndpoints.getScanPhotoData, { key: scanPhotoId, query: query ?? {} });
  }
  getScanPhotoImageUrl(scanPhotoId: string, options?: { maxDimension?: MaxPhotoDimension }) {
    return getScanPhotoImageUrl(scanPhotoId, options);
  }

  getScanPhotosById(scanPhotoIds: string[], options?: { validate?: boolean }) {
    if (scanPhotoIds.length === 0) return Promise.resolve([]);
    return this.jsonRequest(KaiProApiEndpoints.getScanPhotosById, {
      query: options?.validate ? { validate: true } : {},
      payload: { scanPhotoIds },
    });
  }

  // Scan Template
  getScanTemplate(scanTemplateId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getScanTemplate, { key: scanTemplateId });
  }
  createScanTemplate(payload: ApiCreateScanTemplate, scanTemplateRoot?: ScanTemplateRoot) {
    return this.jsonRequest(KaiProApiEndpoints.createScanTemplate, { payload: { ...payload, scanTemplateRoot } });
  }
  updateScanTemplate(scanTemplateId: string, payload: ApiUpdateScanTemplate) {
    return this.jsonRequest(KaiProApiEndpoints.updateScanTemplate, { key: scanTemplateId, payload });
  }
  getScanTemplates(options?: { archived?: boolean }) {
    return this.jsonRequest(KaiProApiEndpoints.getScanTemplates, { query: options?.archived === true ? { archived: true } : {} });
  }

  // Scan Template Data
  saveScanTemplateDataDelta(scanTemplateId: string, delta: ScanDataInMemory[]) {
    return this.jsonRequest(KaiProApiEndpoints.saveScanTemplateDataDelta, { key: scanTemplateId, payload: { data: delta } });
  }
  getScanTemplateRootForScan(scanId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getScanTemplateRootForScan, { key: scanId });
  }
  getScanTemplatePartHistory(scanTemplateId: string, scanPartId: string, options?: ScanPartTreeHistoryOptions) {
    return this.jsonRequest(KaiProApiEndpoints.getScanTemplatePartHistory, {
      key: scanTemplateId,
      query: getScanPartTreeHistoryQuery(scanPartId, options),
    });
  }

  /** Fetches as many pages as needed to return up to `limit` records (all of them if `limit` is omitted). */
  getAllScanTemplatePartHistory(scanTemplateId: string, scanPartId: string, options?: AllScanPartTreeHistoryOptions) {
    return fetchAllScanPartTreeHistoryPages(pageOptions => this.getScanTemplatePartHistory(scanTemplateId, scanPartId, pageOptions), options);
  }

  getScanTemplateRoot(scanTemplateId: string, versionAt?: Date) {
    return this.jsonRequest(KaiProApiEndpoints.getScanTemplateRoot, {
      key: scanTemplateId,
      query: { versionAt: versionAt ? toDate(versionAt).toISOString() : undefined },
    });
  }

  // Scan
  getScan(scanId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getScan, { key: scanId });
  }
  // getScansByOrgId(orgId: string) { return this.jsonRequest(KaiProApiEndpoints.getScansByOrgId, { key: orgId }); }
  getScans(options?: { scanLifecycleStatuses?: ScanLifecycleStatuses; archived?: boolean }) {
    return this.jsonRequest(KaiProApiEndpoints.getScans, {
      query: {
        ...(options?.scanLifecycleStatuses ? { scanLifecycleStatuses: options.scanLifecycleStatuses.join(',') } : {}),
        ...(options?.archived === true ? { archived: true } : {}),
      },
    });
  }
  getScansByPropertyProjectId(propertyProjectId: string, options?: { archived?: boolean; includeAudienceDocs?: boolean }) {
    return this.jsonRequest(KaiProApiEndpoints.getScansByPropertyProjectId, {
      key: propertyProjectId,
      query: {
        ...(options?.archived === true ? { archived: true } : {}),
        ...(options?.includeAudienceDocs === true ? { includeAudienceDocs: true } : {}),
      },
    });
  }
  getScansByScanTemplateId(scanTemplateId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getScansByScanTemplateId, { key: scanTemplateId });
  }
  createScan(payload: ApiCreateScan) {
    return this.jsonRequest(KaiProApiEndpoints.createScan, { payload });
  }
  updateScan(scanId: string, payload: ApiUpdateScan) {
    return this.jsonRequest(KaiProApiEndpoints.updateScan, { key: scanId, payload });
  }

  async getScanData(scanId: string, options?: { from?: Date; to?: Date; includeBaseline?: boolean; syncedAfter?: Date }) {
    const result = await this.jsonRequest(KaiProApiEndpoints.getScanData, {
      key: scanId,
      query: {
        from: options?.from ? toDate(options.from).toISOString() : undefined,
        to: options?.to ? toDate(options.to).toISOString() : undefined,
        syncedAfter: options?.syncedAfter ? toDate(options.syncedAfter).toISOString() : undefined,
        includeBaseline: options?.includeBaseline ?? false,
      },
    });
    return {
      ...result,
      from: toDate(result.from ?? BEGINNING_OF_TIME),
      to: toDate(result.to),
    };
  }

  getScanPartHistory(scanId: string, scanPartId: string, options?: ScanPartTreeHistoryOptions) {
    return this.jsonRequest(KaiProApiEndpoints.getScanPartHistory, { key: scanId, query: getScanPartTreeHistoryQuery(scanPartId, options) });
  }

  /** Fetches as many pages as needed to return up to `limit` records (all of them if `limit` is omitted). */
  getAllScanPartHistory(scanId: string, scanPartId: string, options?: AllScanPartTreeHistoryOptions) {
    return fetchAllScanPartTreeHistoryPages(pageOptions => this.getScanPartHistory(scanId, scanPartId, pageOptions), options);
  }

  getScanRoot(scanId: string, versionAt?: Date) {
    return this.jsonRequest(KaiProApiEndpoints.getScanRoot, { key: scanId, query: { versionAt: versionAt?.toISOString() } });
  }
  saveScanDataDelta(scanId: string, delta: ScanDataInMemory[], timeoutSeconds?: number) {
    return this.jsonRequest(KaiProApiEndpoints.saveScanDataDelta, { key: scanId, payload: { data: delta } }, timeoutSeconds);
  }
  cancelChangeOrder(scanId: string) {
    return this.jsonRequest(KaiProApiEndpoints.cancelChangeOrder, { key: scanId });
  }

  // Scope Report
  exportScopePurchaseOrder(scanId: string, query: { reportType?: ApiReportType } = {}) {
    return this.jsonRequest(KaiProApiEndpoints.exportScopePurchaseOrderExcel, { key: scanId, query });
  }
  exportScopeReportPdf(scanId: string, query: { reportType?: ApiReportType } = {}) {
    return this.jsonRequest(KaiProApiEndpoints.exportScopeReportPdf, { key: scanId, query });
  }

  // ***********************************************************************************
  // Operations (generic async-operation API — see ASYNC_OPERATION_API_RFC.md)
  // ***********************************************************************************
  /**
   * Kick off a client-permitted operation. `input` is typed to the given `operationType`, so a
   * mismatched payload is a compile error. Returns the created record (poll it via {@link getOperation}).
   */
  createOperation<T extends ApiOperationType>(params: { operationType: T; input: ApiOperationOf<T>['input'] }) {
    return this.jsonRequest(KaiProApiEndpoints.createOperation, { payload: params });
  }
  /** Read + poll one operation: full record with status and, narrowed on operationType, typed input/progressDetails/output. */
  getOperation(id: string) {
    return this.jsonRequest(KaiProApiEndpoints.getOperation, { key: id });
  }
  /** List operations for the current org — full records. `status` is sent CSV; `operationType` filters to one type. */
  listOperations(options?: { status?: ApiOperationStatus[]; operationType?: ApiOperationType; limit?: number }) {
    return this.jsonRequest(KaiProApiEndpoints.listOperations, {
      query: {
        ...(options?.status?.length ? { status: options.status.join(',') } : {}),
        ...(options?.operationType ? { operationType: options.operationType } : {}),
        ...(options?.limit != null ? { limit: options.limit } : {}),
      },
    });
  }
  getScopeReportHtml(scanId: string, query: { userId?: string; reportType?: ApiReportType } = {}) {
    return this.getBinary(KaiProApiEndpoints.getScopeReportHtml, { key: scanId, query });
  }
  generateReadOnlyToken() {
    return this.jsonRequest(KaiProApiEndpoints.generateReadOnlyToken, {});
  }
  getScopeReportPhotosZipUrl(scanId: string, token: string) {
    return `${KaiProApiEndpoints.scopeReportPhotosZip.path}/${scanId}?token=${encodeURIComponent(token)}`;
  }

  // Internal Reporting (super-admin only) — see API_STANDARDS.md §"Internal Reporting"
  exportBillingExcel(query: { fromDatetime: string; toDatetime: string }) {
    return this.jsonRequest(KaiProApiEndpoints.exportBillingExcel, { query });
  }
  calcTotalCostsByProductSupplier(payload: { scanIds?: string[]; fromDatetime?: string }) {
    return this.jsonRequest(KaiProApiEndpoints.calcTotalCostsByProductSupplier, { payload });
  }

  // Scan Note
  getScanNotesByScanId(scanId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getScanNotesByScanId, { key: scanId });
  }
  createScanNote(payload: ApiCreateScanNote) {
    return this.jsonRequest(KaiProApiEndpoints.createScanNote, { payload });
  }
  bulkCreateScanNotes(scanId: string, notes: ApiCreateScanNote[], timeoutSeconds?: number) {
    return this.jsonRequest(KaiProApiEndpoints.bulkCreateScanNotes, { key: scanId, payload: { data: notes } }, timeoutSeconds);
  }

  // Scan Data Suggestion
  createScanDataSuggestion(payload: ApiCreateScanDataSuggestion) {
    return this.jsonRequest(KaiProApiEndpoints.createScanDataSuggestion, { payload });
  }
  getScanDataSuggestion(scanDataSuggestionId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getScanDataSuggestion, { key: scanDataSuggestionId });
  }
  updateScanDataSuggestion(scanDataSuggestionId: string, payload: ApiUpdateScanDataSuggestion) {
    return this.jsonRequest(KaiProApiEndpoints.updateScanDataSuggestion, { key: scanDataSuggestionId, payload });
  }
  updateScanDataSuggestionsByScanVideoId(scanVideoId: string, payload: ApiUpdateScanDataSuggestionsByScanVideo) {
    return this.jsonRequest(KaiProApiEndpoints.updateScanDataSuggestionsByScanVideoId, { key: scanVideoId, payload });
  }
  getScanDataSuggestionsByScanId(scanId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getScanDataSuggestionsByScanId, { key: scanId });
  }

  evaluateJev(payload: JevEvaluatePayload) {
    return this.jsonRequest(KaiProApiEndpoints.evaluateJev, { payload });
  }

  // Retailer Product
  searchRetailerProducts(query: RetailerProductSearchRequestQuery) {
    return this.jsonRequest(KaiProApiEndpoints.searchRetailerProducts, { query });
  }
  searchCombinedProducts(query: CombinedProductSearchRequestQuery) {
    return this.jsonRequest(KaiProApiEndpoints.searchCombinedProducts, { query });
  }

  // Market: TODO - deprecate then rename all to (e.g.) getOrgMarket - not getMarket - for consistency
  getMarket(marketId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getMarket, { key: marketId });
  }
  getOrgMarkets(options?: { archived?: boolean; search?: string }) {
    return this.jsonRequest(KaiProApiEndpoints.getOrgMarkets, {
      query: {
        ...(options?.archived === true ? { archived: true } : {}),
        ...(options?.search?.trim() ? { search: options.search.trim() } : {}),
      },
    });
  }
  getMarketsByZipCode(zipCode: string) {
    return this.jsonRequest(KaiProApiEndpoints.getMarketsByZipCode, { query: { zipCode } });
  }
  createMarket(payload: SafeRecordWithCustomerMetadata<ApiCreateOrgMarket>) {
    return this.jsonRequest(KaiProApiEndpoints.createMarket, { payload });
  }
  bulkCreateMarkets(markets: SafeRecordWithCustomerMetadata<ApiCreateOrgMarket>[]) {
    return this.jsonRequest(KaiProApiEndpoints.bulkCreateMarkets, { payload: { data: markets } });
  }
  updateMarket(marketId: string, payload: SafeRecordWithCustomerMetadata<ApiUpdateOrgMarket>) {
    return this.jsonRequest(KaiProApiEndpoints.updateMarket, { key: marketId, payload });
  }
  addContractorToMarket(marketId: string, orgContractorId: string) {
    return this.jsonRequest(KaiProApiEndpoints.addContractorToMarket, { key: marketId, query: { orgContractorId } });
  }
  removeContractorFromMarket(marketId: string, orgContractorId: string) {
    return this.jsonRequest(KaiProApiEndpoints.removeContractorFromMarket, { key: marketId, query: { orgContractorId } });
  }
  getUsersByOrgMarketId(orgMarketId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getUsersByOrgMarketId, { key: orgMarketId });
  }
  getMarketsByUserId(userId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getMarketsByUserId, { key: userId });
  }
  addUserToOrgMarket(orgMarketId: string, userId: string) {
    return this.jsonRequest(KaiProApiEndpoints.addUserToOrgMarket, { key: orgMarketId, query: { userId } });
  }
  removeUserFromOrgMarket(orgMarketId: string, userId: string) {
    return this.jsonRequest(KaiProApiEndpoints.removeUserFromOrgMarket, { key: orgMarketId, query: { userId } });
  }

  // Contractor
  getOrgContractors(options?: { archived?: boolean; search?: string }) {
    return this.jsonRequest(KaiProApiEndpoints.getOrgContractors, {
      query: {
        ...(options?.archived === true ? { archived: true } : {}),
        ...(options?.search?.trim() ? { search: options.search.trim() } : {}),
      },
    });
  }
  getContractorsByOrgMarketId(orgMarketId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getContractorsByOrgMarketId, { key: orgMarketId });
  }
  getContractor(contractorId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getContractor, { key: contractorId });
  }
  createContractor(payload: SafeRecordWithCustomerMetadata<ApiCreateOrgContractor>) {
    return this.jsonRequest(KaiProApiEndpoints.createContractor, { payload });
  }
  bulkCreateContractors(contractors: SafeRecordWithCustomerMetadata<ApiCreateOrgContractor>[]) {
    return this.jsonRequest(KaiProApiEndpoints.bulkCreateContractors, { payload: { data: contractors } });
  }
  updateContractor(contractorId: string, payload: SafeRecordWithCustomerMetadata<ApiUpdateOrgContractor>) {
    return this.jsonRequest(KaiProApiEndpoints.updateContractor, { key: contractorId, payload });
  }
  addMarketToContractor(orgContractorId: string, orgMarketId: string) {
    return this.jsonRequest(KaiProApiEndpoints.addMarketToContractor, { key: orgContractorId, query: { orgMarketId } });
  }
  removeMarketFromContractor(orgContractorId: string, orgMarketId: string) {
    return this.jsonRequest(KaiProApiEndpoints.removeMarketFromContractor, { key: orgContractorId, query: { orgMarketId } });
  }
  bulkCreateContractorMarketRelationships(relationships: { orgContractorId: string; orgMarketId: string }[]) {
    return this.jsonRequest(KaiProApiEndpoints.bulkCreateContractorMarketRelationships, { payload: { data: relationships } });
  }

  // Contractor Link
  createContractorScanShareLink(scanId: string, orgContractorId: string) {
    return this.jsonRequest(KaiProApiEndpoints.createContractorScanShareLink, { key: scanId, query: { orgContractorId } });
  }
  getContractorLinkInfo(token: string) {
    return this.jsonRequest(KaiProApiEndpoints.getContractorLinkInfo, { key: token });
  }
  initiateContractorClaim(payload: { token: string; channel?: 'email' | 'sms' }) {
    return this.jsonRequest(KaiProApiEndpoints.initiateContractorClaim, { payload });
  }
  claimContractorOrg(token: string) {
    return this.jsonRequest(KaiProApiEndpoints.claimContractorOrg, { payload: { token } });
  }
  getContractorProjects() {
    return this.jsonRequest(KaiProApiEndpoints.getContractorProjects, {});
  }

  // Audience Doc Share
  createAudienceDocShareLink(scanId: string) {
    return this.jsonRequest(KaiProApiEndpoints.createAudienceDocShareLink, { key: scanId });
  }
  getAudienceDocShareLinkStatus(scanId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getAudienceDocShareLinkStatus, { key: scanId });
  }
  revokeAudienceDocShareLink(scanId: string) {
    return this.jsonRequest(KaiProApiEndpoints.revokeAudienceDocShareLink, { key: scanId });
  }

  // OrgProduct
  getOrgProducts(options?: { archived?: boolean; queryString?: string; limit?: number }) {
    return this.jsonRequest(KaiProApiEndpoints.getOrgProducts, {
      query: {
        ...(options?.archived === true ? { archived: true } : {}),
        ...(options?.queryString ? { queryString: options.queryString } : {}),
        ...(options?.limit ? { limit: options.limit.toString() } : {}),
      },
    });
  }
  getOrgProduct(orgProductId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getOrgProduct, { key: orgProductId });
  }
  getOrgProductBatch(orgProductIds: string[]) {
    return this.jsonRequest(KaiProApiEndpoints.getOrgProductBatch, { query: { orgProductIds: orgProductIds.join(',') } });
  }
  vivifyOrgProduct(payload: SafeRecordWithCustomerMetadata<ApiCreateOrgProduct>) {
    return this.jsonRequest(KaiProApiEndpoints.vivifyOrgProduct, { payload });
  }
  updateOrgProduct(orgProductId: string, payload: SafeRecordWithCustomerMetadata<ApiUpdateOrgProduct>) {
    return this.jsonRequest(KaiProApiEndpoints.updateOrgProduct, { key: orgProductId, payload });
  }

  // OrgSettings (deprecated)
  getOrgSetting(orgSettingsId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getOrgSetting, { key: orgSettingsId });
  }
  getOrgSettings() {
    return this.jsonRequest(KaiProApiEndpoints.getOrgSettings, {});
  }
  updateOrgSettingsByKey(settingsKey: string, payload: ApiUpdateOrgSettings) {
    return this.jsonRequest(KaiProApiEndpoints.updateOrgSettingsByKey, { key: settingsKey, payload });
  }

  // AllOrgSettings
  getAllOrgSettings() {
    return this.jsonRequest(KaiProApiEndpoints.getAllOrgSettings, {});
  }
  updateAllOrgSettings(payload: ApiAllOrgSettings) {
    return this.jsonRequest(KaiProApiEndpoints.updateAllOrgSettings, { payload });
  }

  // UserToScan
  getUsersForScan(scanId: string) {
    return this.jsonRequest(KaiProApiEndpoints.getUsersForScan, { key: scanId });
  }
  assignUserToScan(userId: string, scanId: string) {
    return this.jsonRequest(KaiProApiEndpoints.assignUserToScan, { payload: { userId, scanId } });
  }
  removeUserFromScan(userId: string, scanId: string) {
    return this.jsonRequest(KaiProApiEndpoints.removeUserFromScan, { payload: { userId, scanId } });
  }
  getScansForUser(userId: string, limit?: number) {
    return this.jsonRequest(KaiProApiEndpoints.getScansForUser, { key: userId, query: { limit } });
  }
}
